
export interface Meeting {
  id: string;
  user_id?: string;
  meeting_name: string;
  meeting_category?: string;
  meeting_region?: string;
  meeting_link: string;
  meeting_password?: string;
  meeting_period: string;
  meeting_time: string;
  meeting_weekday: string;
  meeting_place: string;
  description?: string;
  group_id?: string;
  header_image_url?: string | null;
  meeting_type?: string;
  // Elo-sourced extensions (optional, present when source === 'elo')
  // v2.0.0: `community` and `category` are gone from the API. `meeting_label`
  // and `meeting_notes` are operator-controlled metadata on the meet itself.
  meeting_label?: string | null;
  meeting_notes?: string | null;
  meet_number?: number | null;
  // Plus paywall — kept defensively. v2.0.0 strips `access` from the meet
  // payload so these resolve to defaults until Elo re-introduces gating.
  access_tier?: 'free' | 'plus' | string | null;
  plus_required?: boolean;
  is_plus?: boolean;
  invite_required?: boolean;
  meeting_password_locked?: boolean;
  source?: 'supabase' | 'elo';
  // Phase 2 split (PRD_MEETINGS §6): the server now ships `next_session`
  // (single upcoming occurrence) + `sessions[]` (all recurring slots).
  // `meeting_time` / `meeting_weekday` above are derived from these
  // client-side by `normalizeMeetingFromApi()` in `services/meetingsService.ts`
  // — the legacy server dual-write was removed in Phase 3b (May 2026).
  next_session?: {
    session_id?: string;
    weekday?: string;
    time?: string;
    starts_at_iso?: string | null;
    category?: string | null;
  } | null;
  sessions?: Array<{
    id?: string;
    weekday?: string;
    time?: string;
    period?: string | null;
    category?: string | null;
  }>;
  created_at: string;
  updated_at: string;
}

export interface MeetingFilters {
  times?: string[];
  days?: string[];
  search?: string;
  period?: string;
  category?: string;
  region?: string;
}

export interface MeetingCardProps {
  meeting: Meeting;
  isSaved: boolean;
  onToggleSaved: (meetingId: string) => void;
  onJoinMeeting: (meetingLink: string) => void;
}

export type MeetingExperience =
  | 'never'
  | 'once'
  | 'few_times'
  | 'long_periods'
  | 'for_someone';

export type MeetingIntent =
  | 'iniciante'
  | 'novo'
  | 'visitante'
  | 'recaido'
  | 'itinerante'
  | 'codependente';

export type MeetingNeed =
  | 'courage'
  | 'information'
  | 'emotional_support'
  | 'talk_to_others'
  | 'recovery_after_relapse'
  | 'learn_to_help';

export type MeetingProfile =
  | 'iniciante'
  | 'novo'
  | 'visitante'
  | 'recaido'
  | 'itinerante'
  | 'codependente';

export interface UserMeetingPreferences {
  id: string;
  user_id: string;
  meeting_experience: MeetingExperience | null;
  meeting_intent: MeetingIntent | null;
  meeting_need: MeetingNeed | null;
  derived_profile: MeetingProfile | null;
  created_at: string;
  updated_at: string;
}

// Canonical meeting_type taxonomy. Extended in Task #124 (May 2026) to add
// `reuniao_feminina` and `reuniao_meditacao` — see
// documentation/05. Meetings/FEAT_MEETING_ONBOARDING.md for the full
// category → type map and the scoring algorithm that consumes these.
export type MeetingType =
  | 'reuniao_novos'
  | 'reuniao_partilhas'
  | 'reuniao_tematica'
  | 'reuniao_estudo_literatura'
  | 'reuniao_familia'
  | 'reuniao_codependentes'
  | 'reuniao_ateus_agnosticos'
  | 'reuniao_lgbtqiapn'
  | 'reuniao_feminina'
  | 'reuniao_meditacao';

// Profile → meeting_type matrix. `reuniao_feminina` joins `reuniao_partilhas`
// for every addict profile (iniciante / novo / visitante / recaido /
// itinerante); `reuniao_meditacao` joins `reuniao_tematica` for `novo` and
// `itinerante`. Codependente stays addict-type-free — family routing is
// handled separately server-side via the addiction→family-group map.
export const PROFILE_MEETING_TYPES: Record<MeetingProfile, MeetingType[]> = {
  iniciante: ['reuniao_novos', 'reuniao_partilhas', 'reuniao_feminina'],
  novo: ['reuniao_tematica', 'reuniao_novos', 'reuniao_estudo_literatura', 'reuniao_meditacao', 'reuniao_feminina'],
  visitante: ['reuniao_partilhas', 'reuniao_ateus_agnosticos', 'reuniao_lgbtqiapn', 'reuniao_feminina'],
  recaido: ['reuniao_partilhas', 'reuniao_novos', 'reuniao_tematica', 'reuniao_feminina'],
  itinerante: ['reuniao_partilhas', 'reuniao_tematica', 'reuniao_estudo_literatura', 'reuniao_meditacao', 'reuniao_feminina'],
  codependente: ['reuniao_familia', 'reuniao_codependentes'],
};
